import React from 'react';
import {FaIcon} from '../FaIcon';

/**
 * Las piezas visuales de las notas de filtro (`utils/filterNotes.js`), compartidas por Informes y
 * el Explorador: el ⓘ con los motivos, el «—» de una métrica que no aplica y la lista de notas.
 *
 * El ⓘ es el gris informativo del Tablero (`info-icon-container -stronger` + `warning-tooltip`),
 * no un triángulo: no pide nada, explica. Se alcanza con teclado (`tabIndex` + el `:focus-visible`
 * global). El `stopPropagation` es por el encabezado de la tabla, que ordena al hacer clic.
 *
 * ⚠️ Todo en `<span>`, y no en el `<div>` / `<p>` del patrón canónico: el encabezado de la tabla
 * (`ColumnTitle`) es un `<p>`, y un bloque adentro de un `<p>` lo cierra antes de tiempo —el
 * navegador parte el encabezado y React avisa «<p> cannot contain a nested <div>». El SCSS de
 * `.fn-info` les da el `display: block` que al cartel le daban los tags.
 *
 * El cartel se abre en `position: fixed`, calculado al entrar (mouse o foco) y acomodado a la
 * ventana, igual que el «?» de ayuda del mismo encabezado (`FieldHelpIcon`): anclado al ⓘ vivía
 * dentro de `.table-box`, que tiene `overflow: auto`, y en las columnas de la derecha o a 390 la caja
 * lo recortaba a 30 px (QA-VIS-filtrosfe-30).
 */

// Distancia mínima entre el cartel y el borde de la ventana.
const VIEWPORT_MARGIN = 8;
const TRIGGER_GAP = 6;

// Un nombre de campo entre comillas («Meta Ads - Edad») se lee como una unidad: no se parte en el
// guion al final del renglón (QA-VIS-filtrosfe-26). Se envuelve al DIBUJAR, no en el texto, para que
// el texto siga siendo texto (tests, PDF, `aria-label`).
const QUOTED_NAME = /(«[^»]+»)/g;

/** El texto con cada «nombre» en un `span` que no se parte. Un texto sin comillas sale tal cual. */
export function renderNoted(text) {
    if (typeof text !== 'string' || !text.includes('«')) return text;
    return text.split(QUOTED_NAME).map((part, index) => (
        part.startsWith('«') && part.endsWith('»')
            ? <span key={index} className="fn-nowrap">{part}</span>
            : part
    ));
}

function tooltipOf(trigger) {
    return trigger ? trigger.querySelector('.warning-tooltip') : null;
}

/** Abre el cartel flotante: `fixed`, debajo (o encima) del ⓘ, sin salirse de la ventana. */
export function openFloatingTooltip(trigger, position) {
    const tip = tooltipOf(trigger);
    if (!tip) return;
    const rect = trigger.getBoundingClientRect();
    tip.classList.add('-floating');
    tip.style.display = 'block';
    tip.style.position = 'fixed';
    tip.style.zIndex = '100000';
    tip.style.right = 'auto';
    tip.style.bottom = 'auto';
    tip.style.transform = 'none';
    // Se mide ya visible: con display none el ancho es 0.
    const width = tip.offsetWidth || 340;
    const height = tip.offsetHeight || 0;
    const viewportWidth = window.innerWidth || 0;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(rect.left, viewportWidth - width - VIEWPORT_MARGIN));
    const top = String(position).startsWith('above') ? rect.top - height - TRIGGER_GAP : rect.bottom + TRIGGER_GAP;
    tip.style.left = `${left}px`;
    tip.style.top = `${Math.max(VIEWPORT_MARGIN, top)}px`;
}

/** Lo deja como estaba: sin estilos inline, el CSS vuelve a mandar (`display: none` en reposo). */
export function closeFloatingTooltip(trigger) {
    const tip = tooltipOf(trigger);
    if (!tip) return;
    tip.classList.remove('-floating');
    ['display', 'position', 'zIndex', 'right', 'bottom', 'transform', 'left', 'top'].forEach((prop) => {
        tip.style[prop] = '';
    });
}

export function FilterNotesInfoIcon({texts, position = 'below-left', testId = 'filter-notes-info'}) {
    if (!texts || !texts.length) return null;
    const open = (e) => openFloatingTooltip(e.currentTarget, position);
    const close = (e) => closeFloatingTooltip(e.currentTarget);
    return (
        <span className="info-icon-container -stronger fn-info" tabIndex={0} aria-label={texts.join(' ')}
              data-testid={testId} onClick={(e) => e.stopPropagation()}
              onMouseEnter={open} onMouseLeave={close} onFocus={open} onBlur={close}>
            <FaIcon iconName="info-circle"/>
            <span className={`warning-tooltip -rich -${position}`}>
                <span className="warning-tooltip-body">
                    {texts.map((text) => <span key={text} className="warning-tooltip-text">{renderNoted(text)}</span>)}
                </span>
            </span>
        </span>
    );
}

/** «—» con el motivo al lado. Para una celda o un Total. */
export function NotApplicableValue({message, position = 'above-left', testId = 'filter-notes-na'}) {
    return (
        <span className="fn-na" data-testid={testId}>
            <span className="fn-na-dash" aria-hidden="true">—</span>
            {message && <FilterNotesInfoIcon texts={[message]} position={position} testId={`${testId}-info`}/>}
        </span>
    );
}

/** Las notas escritas, debajo de un valor o de un gráfico. */
export function FilterNotesList({texts, testId = 'filter-notes-list'}) {
    if (!texts || !texts.length) return null;
    return (
        <ul className="fn-notes" data-testid={testId}>
            {texts.map((text) => <li key={text}>{renderNoted(text)}</li>)}
        </ul>
    );
}
